import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  Grid,
  IconButton,
  Stack,
  Tab,
  Typography,
} from '@mui/material';
import { CaseInformation, CaseSummary, ContactForm } from 'src/careflow/care-coordination/case';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Page from 'src/components/Page';
import { PATH_DASHBOARD } from 'src/routes/paths';
import useSettings from 'src/careflow/ph-core/multi-tenant/setting/useSettings';
import {
  useCarePlan,
  useCareTeams,
  useCommunications,
  useConditions,
  useDocumentReferences,
  useGoals,
  useObservations,
  usePatient,
  usePlanDefinitions,
  useServiceRequests,
  useTasks,
} from 'src/careflow/ph-core/fhir-client/fhir-react';
import {
  PatientWrapper,
  WrappedPatient,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import {
  CarePlanWrapper,
  WrappedCarePlan,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import { useParams } from 'react-router';
import {
  ServiceRequestWrapper,
  WrappedServiceRequest,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import _, { isArray, isEmpty, isObject, sortBy } from 'lodash';
import NoteAddForm from 'src/careflow/common/NoteAddForm';
import CaseCareTeamGrid from 'src/careflow/care-coordination/case/detail/components/CaseCareTeamGrid';
import { CareTeamWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CareTeam';
import {
  PlanDefinitionWrapper,
  WrappedPlanDefinition,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PlanDefinition';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { getParseColumn } from 'src/careflow/ph-core/workflow/helpers/util';
import { TABLE_HEAD_ASSESSMENTS, TABLE_HEAD_CONTACT } from 'src/careflow/common/table-head';
import {
  mapAssessmentsForms,
  mapCommunicationsToNotesDisplay,
  mapContactAttemptsCases,
} from 'src/careflow/common/common-utils';
import Iconify from 'src/components/Iconify';
import { TableCustom } from 'src/components/TableCustom';
import { NotesDisplayWithAdd, CRS_NOTES_COLUMNS } from 'src/components/NotesDisplay';
import { DocumentList } from 'src/careflow/care-coordination/patient/attachments/DocumentList';
import {
  AssessmentsFormsResponse,
  ReferralService,
} from 'src/crs/referral/services/ReferralService';
import OutboundByPathway from '../../referral/components/outboundGrid/OutboundByPathway';
import StartAssessmentsButton from 'src/careflow/care-coordination/patient/assessment/list/StartAssessmentsButton';
import {
  checkAclValidation,
  getRelatedAcls,
} from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import RelatedReferrals from 'src/careflow/care-coordination/case/detail/components/RelatedReferrals/RelatedReferrals';
import {
  filterTasksByDefinition,
  includeSubtasksOf,
  getGoalsIds,
  getParentGoalGridRows,
  getReferenceResourceId,
  getTaskGridRows,
  translateTableHead,
  fetchOrganizationAffiliation,
  getTaskBasedOn,
} from 'src/careflow/helpers/common';
import useHealthCareServicesByServiceRequests from 'src/hooks/useHealthCareServicesByServiceRequests';
import { CarePlan } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/CarePlan';
import { Communication } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/Communication';
import { Annotation } from 'src/careflow/ph-core/fhir-client/resources';
import produce from 'immer';
import ExpandableCard from 'src/components/common/ExpandableCard';
import { ConditionsList } from 'src/careflow/care-coordination/patient/conditions/ConditionsList';
import { ConditionWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import ComplementaryInfo from 'src/components/complementary-info/ComplementaryInfo';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import {
  GoalsPermissions,
  ResourceWithIncludedResources,
  TaskPermissions,
} from 'src/careflow/helpers/types';
import { Add, CheckSharp, Lock, PlayArrowSharp } from '@mui/icons-material';
import { CollapsibleProvider } from 'src/contexts/CollapsibleContext';
import { ObservationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import ConsentInformation, {
  getSelectOptionCustomButton as getConsentOption,
} from 'src/careflow/engagement-hub/patient-engagement/patient/components/workflow-step/ConsentInformation';
import { getRegistries } from 'src/careflow/ph-core/multi-tenant/registry/registry';
import { WrappedConsent } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Consent';
import ConsentGrid from 'src/careflow/care-coordination/patient/Consent/ConsentGrid';
import ScheduleAppointment from 'src/careflow/engagement-hub/patient-engagement/patient/components/workflow-step/ScheduleAppointment';
import { formattedDateTimeZone } from 'src/utils/dates';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import {
  buildMappingChecklistItem,
  buildModifiedFieldsFromData,
  getConsents,
  handlerGenericStepWorkflow,
  workflowInstanceData,
} from 'src/careflow/ph-core/workflow/helpers/workflow';
import AssessmentFromChecklistData, {
  AssessmentChecklistDataButton,
} from 'src/careflow/engagement-hub/patient-engagement/patient/components/workflow-step/AssessmentFromChecklistData';
import AssessmentFrame from 'src/careflow/care-coordination/patient/assessment/frame/AssessmentFrame';
import { AppointmentList } from 'src/careflow/care-coordination/patient/appointments/AppointmentList';
import { GoalWrapper, WrappedGoal } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Goal';
import { AlertSeverity } from 'src/components/SeverityStatus';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import { NonExistingInformation } from 'src/careflow/ph-core/workflow/components/common/ui/NonExistingInformation';
import { useDialogStore } from 'src/stores/dialog';
import { WorkflowHeader } from 'src/careflow/ph-core/workflow/common/WorkflowHeader';
import useRevisitTaskValidation from 'src/hooks/useRevisitTaskValidation';
import Workflow, { WorkflowData } from 'src/careflow/ph-core/workflow/Workflow';
import IconRequired from 'src/assets/icon_required.svg';
import { WorkflowStatistics } from 'src/careflow/ph-core/workflow/common/WorkflowStatistics';
import { CREATE_GOAL, CREATE_TASK, UPDATE_GOAL, UPDATE_TASK, useEventBus } from 'src/event-bus';
import { getIdFromReference } from 'src/utils/fhir';
import { WorkflowService } from 'src/careflow/ph-core/workflow/api/WorkflowService';
import { registerSteps } from 'src/careflow/ph-core/workflow/registry/workflowRegistry';
import { UserService } from 'src/careflow/ph-core/admin/user/UserService';
import ObservationDetails from 'src/careflow/care-coordination/patient/observations/ObservationDetails';
import GoalContextProvider from 'src/careflow/care-coordination/case/goal/provider';
import PatientGenericServiceRequest, {
  ServiceRequestsPermissions,
} from 'src/careflow/care-coordination/patient/service-request/PatientGenericServiceRequest';
import GroupsList from 'src/careflow/care-coordination/patient/groups/GroupList';
import {
  getStatusCountByPathway,
  SeverityStatusCount,
} from 'src/careflow/care-coordination/case/detail/components/CaseTaskGrid';
import GoalsByPathway, {
  getStatusCountByPathway as getGoalStatusCountByPathway,
} from 'src/careflow/care-coordination/case/goal/pathway/GoalsByPathway';
import { RelatedPersonList } from 'src/careflow/care-coordination/patient/related-person/RelatedPersonList';
import TasksByPathway from 'src/careflow/care-coordination/task/patient/TasksByPathway';
import { commonMenuTaskHandlers } from 'src/careflow/care-coordination/task/common/handlers';
import PatientSummary from '../../patient/detail/components/PatientSummary';
import FamilyMembers from './components/FamilyMembers';
import { assignOwnerHandler } from 'src/careflow/ph-core/workflow/handlers';
import { useTaskActivityDefinition, useTaskAssessment } from 'src/careflow/helpers/hooks';
import useUserPractitionerRole from 'src/hooks/useUserPractitionerRole';
import {
  CaseContactAttemptResponse,
  CaseResponse,
  CaseService,
} from 'src/crs/case/service/CaseService';
import { CommunicationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Communication';
import { DocumentReferenceWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/DocumentReference';
import { TaskWrapper, WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { parseShortFormat } from 'src/utils/formatTime';
import { showCloseModal } from 'src/careflow/ph-core/workflow/components/common/dialog/CloseWorkflowDialog';
import StandardDialogProvider, {
  useStandardDialog,
} from 'src/careflow/ph-core/ui/base/StandardDialogProvider';
import { PrintableCarePlan } from '../printable-care-plan/PrintableCarePlan';

export default function CaseDetails() {
  const user = useAuth();
  const { i18n } = useLocales();
  const { confirmDialog, closeDialog } = useDialogStore();
  const { themeStretch } = useSettings();
  const { onEvent, removeEvent } = useEventBus();
  const standardDialog = useStandardDialog();

  const { id: carePlanId = null } = useParams();
  const [patientId, setPatientId] = useState<string | null>(null);
  const [workflowInstance, setWorkflowInstance] = useState<any>({});
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [contactAttempt, setContactAttempt] = useState<CaseContactAttemptResponse[]>([]);
  const [openCollapse, setOpenCollapse] = useState(false);
  const [openCollapseRelatedReferrals] = useState(true);
  const [assessmentsForms, setAssessmentsForms] = useState<AssessmentsFormsResponse[]>([]);
  const [initialWorkflow, setInitialWorkflow] = useState<any>(null);

  const [relatedPersonsOpen, setRelatedPersonsOpen] = useState(false);
  const { componentsData, configurations } = useTenantConfigData();
  const [statisticsData, setStatisticsData] = useState<any>(null);

  const [
    carePlan,
    { isSuccess: isSuccessCarePlan, update: updateCarePlan, refresh: refreshCarePlan },
  ] = useCarePlan(carePlanId, { map: CarePlanWrapper });
  const [patient] = usePatient(patientId, {
    map: PatientWrapper,
  });

  const [
    serviceRequest,
    {
      isFetching: isFetchingServiceRequest,
      refresh: refreshServiceRequest,
      create: createServiceRequest,
      update: updateServiceRequest,
    },
  ] = useServiceRequests({
    filter: { subject: patient?.id },
    map: ServiceRequestWrapper,
    autofetch: !!patient?.id,
  });

  const [communicationsQuery, setCommunicationsQuery] = useState({});

  const [communications, { refresh: refreshCommunications, create: createCommunication }] =
    useCommunications({
      filter: communicationsQuery,
      map: CommunicationWrapper,
    });

  const filterByCase = componentsData?.case?.attachments?.byCase;
  const filter = filterByCase
    ? { filter: { related: carePlan?.id }, map: DocumentReferenceWrapper }
    : { filter: { subject: patientId }, map: DocumentReferenceWrapper };
  const [documentReferences, { refresh: refreshDocumentReference }] = useDocumentReferences(filter);

  const [tasks, { refresh: refreshTasks }] = useTasks({
    filter: (() => {
      if (componentsData?.task?.isIncludePatientTask && patient?.id) {
        return { subject: patient.id };
      }

      const relatedTaskIds = carePlan?.getRelatedTaskIds();
      if (relatedTaskIds?.length) {
        return { _id: relatedTaskIds.join(',') };
      }

      return { 'based-on': `CarePlan/${carePlanId}` };
    })(),
    autofetch: componentsData?.task?.isIncludePatientTask
      ? !!patient?.id
      : !!carePlan?.getRelatedTaskIds()?.length || !!carePlanId,
    map: TaskWrapper,
  });

  const [allTasks, { refresh: refreshAllTasks }] = useTasks({
    filter: { patient: patient?.id },
    map: TaskWrapper,
    autofetch: Boolean(!!patient?.id),
  });

  const {
    valueSets: [taskStatuses, goalStatuses],
  } = useValueSetsByIdentifiers(['ph-task-status', 'ph-goal-lifecycle-status']);

  const taskActivityDefinition = useTaskActivityDefinition();

  const taskFilterByActivityDefinition = useMemo(() => {
    const caseTasks = filterTasksByDefinition(tasks, taskActivityDefinition?.case);
    return includeSubtasksOf(tasks, caseTasks);
  }, [tasks, taskActivityDefinition]);

  const taskReScreening = useMemo(() => {
    const tasksBasedOn = getTaskBasedOn(`CarePlan/${carePlanId}`, allTasks);
    const caseTasks = filterTasksByDefinition(tasksBasedOn, taskActivityDefinition?.reScreening);
    return includeSubtasksOf(caseTasks, allTasks);
  }, [allTasks, taskActivityDefinition, carePlanId]);

  const groupedGoals = useMemo(() => carePlan?.getGroupedGoals(), [carePlan]);
  const [goals, { refresh: refreshGoals }] = useGoals({
    filter: {
      _id: getGoalsIds(groupedGoals),
    },
    map: GoalWrapper,
    autofetch: !!getGoalsIds(groupedGoals),
  });

  const { practitionerRoles } = useUserPractitionerRole();

  useEffect(() => {
    if (goals && tasks && goals.length > 0 && tasks.length > 0 && groupedGoals) {
      const goalTaskAssociations: { [taskId: string]: WrappedGoal } = {};
      Object.keys(groupedGoals).forEach((goalId) => {
        const taskIds = groupedGoals[goalId];
        const associatedGoal = goals.find((goal) => goal.id === goalId);

        if (associatedGoal && taskIds.length > 0) {
          taskIds.forEach((taskId) => {
            goalTaskAssociations[taskId] = associatedGoal;
          });
        }
      });
      (window as any).goalTaskAssociations = goalTaskAssociations;
      (window as any).groupedGoals = groupedGoals;
      (window as any).goalsData = goals;
      (window as any).tasksData = tasks;
    } else {
    }
  }, [goals, tasks, groupedGoals]);

  const [careTeams, { refresh: refreshCareTeams }] = useCareTeams({
    map: CareTeamWrapper,
    filter: {
      _id: carePlan?.getCareTeamIds().join(','),
    },
    autofetch: !!carePlan?.getCareTeamIds().length,
  });

  const [planDefinitionsRecords] = usePlanDefinitions({
    filter: {
      status: 'active',
      topic: 'goal',
    },
    map: PlanDefinitionWrapper,
  });

  const [conditions] = useConditions({
    filter: {
      patient: patient?.id,
    },
    map: ConditionWrapper,
    autofetch: !!patient?.id,
  });

  const [observations] = useObservations({
    filter: {
      patient: patient?.id,
      'based-on': carePlan?.id,
    },
    map: ObservationWrapper,
    autofetch: !!patient?.id,
  });

  const planDefinitions = useMemo(
    () =>
      planDefinitionsRecords.reduce<WrappedPlanDefinition[]>(
        (distinctPlanDefinitions, planDefinition) => {
          if (
            distinctPlanDefinitions.find(
              ({ getGoalDescription }) =>
                getGoalDescription() === planDefinition.getGoalDescription()
            )
          )
            return distinctPlanDefinitions;

          return [...distinctPlanDefinitions, planDefinition];
        },
        []
      ),
    [planDefinitionsRecords]
  );

  const [caseRecord, setCaseRecord] = useState<CaseResponse | null>(null);
  const caseRecordRef = useRef<string | null>(null);
  const untilDisposeShownRef = useRef(false);
  const [workflowStage, setWorkflowStage] = useState(undefined);
  const [disposition, setDisposition] = useState(false);
  const [reject, setReject] = useState(false);

  const [modals, setModals] = useState({
    note: false,
    openDialog: false,
    contactClientForm: false,
    consents: false,
    scheduleAppointment: false,
    followUp: false,
    assessment: false,
  });

  const toggleModal = useCallback((key: keyof typeof modals, value: boolean) => {
    setModals((prev) => ({ ...prev, [key]: value }));
  }, []);

  const [openDispositionExternal, setOpenDispositionExternal] = useState(false);

  const [checklistData, setChecklistData] = useState<any>({});
  const [metadataWorkflow, setMetadataWorkflow] = useState<any>({});
  const userEmail = user?.getCurrentUser?.()?.email;
  const [caseOption, setCaseOption] = useState<any>([]);
  const [consents, setConsents] = useState<ResourceWithIncludedResources<WrappedConsent>[]>([]);
  const [refreshConsents, setRefreshConsents] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getInboundReferrals = useCallback(
    (carePlan: WrappedCarePlan | null, outboundReferrals: WrappedServiceRequest[]): string[] => {
      const inboundReferrals =
        carePlan?.supportingInfo?.reduce?.<string[]>((inboundReferrals, { reference }) => {
          const referralId = getReferenceResourceId(reference ?? '');
          //if serviceRequestId is in outboundReferrals array then can't be considered as an Inbound
          if (outboundReferrals.find(({ id }) => id === referralId)) return inboundReferrals;
          if (!referralId) return inboundReferrals;
          inboundReferrals.push(referralId);
          return inboundReferrals;
        }, []) ?? [];
      return inboundReferrals;
    },
    []
  );

  const getServiceRequestByFilters: WrappedServiceRequest[] = useMemo(() => {
    const categoryFilter = componentsData?.case?.outboundReferrals?.categoryFilter ?? [];
    return (
      serviceRequest?.reduce((acc: WrappedServiceRequest[], current) => {
        const hasMatchingCategory = current?.category?.some((c) =>
          c?.coding?.some((coding) => coding?.code && categoryFilter.includes(coding.code))
        );
        const isBasedOnCarePlan = current?.basedOn?.some(
          (b) => b?.reference === `CarePlan/${carePlanId}`
        );
        if (hasMatchingCategory && isBasedOnCarePlan) {
          acc.push(current);
        }
        return acc;
      }, []) || []
    );
  }, [serviceRequest, carePlanId, componentsData?.case?.outboundReferrals?.categoryFilter]);

  const inboundReferralIds = useMemo(
    () => getInboundReferrals(carePlan, getServiceRequestByFilters),
    [carePlan, getInboundReferrals, getServiceRequestByFilters]
  );

  const { healthCareServices } = useHealthCareServicesByServiceRequests(
    inboundReferralIds,
    '',
    true
  );

  const getCaseOptionRegistry = useCallback(async () => {
    setIsLoading(true);
    const data = await getRegistries('case', 'case-option');
    const dataKeyValue = data?.[0]?.keyValue ? JSON.parse(data?.[0]?.keyValue) : null;
    setIsLoading(false);
    return dataKeyValue;
  }, []);

  useEffect(() => {
    const getCaseOptionRegistryAsync = async () => {
      const caseOption = await getCaseOptionRegistry();
      setCaseOption(caseOption);
    };
    getCaseOptionRegistryAsync();
  }, [getCaseOptionRegistry]);

  useEffect(() => {
    const getConsentsAsync = async () => {
      const consents = await getConsents(patient);
      setConsents(consents);
      setRefreshConsents(false);
    };
    getConsentsAsync();
  }, [patient, refreshConsents]);

  /*
   ** Workflow Item Handlers
   */
  const assignCaseItemHandler = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    await assignOwnerHandler({
      record: caseRecord,
      workflowInstance,
      userEmail,
      referenceValue: carePlanId,
      referenceName: caseRecord?.workflowReferenceName,
      checklistItem,
      checklistData,
      more,
      onFinally: async () => {
        await fetchCaseRecord(carePlanId);
        await fetchWorkflowData();
      },
    });
  };
  const createWorkflowHandler = useCallback(
    (modalKey: keyof typeof modals) =>
      ({ checklistItem }: any) => {
        toggleModal(modalKey, true);
        setChecklistData(checklistItem);
      },
    [toggleModal]
  );
  const contactAttemptsHandler = createWorkflowHandler('contactClientForm');
  const handlerConsents = createWorkflowHandler('consents');
  const handlerConfirmationScheduleAppointment = createWorkflowHandler('scheduleAppointment');
  const followUpHandler = createWorkflowHandler('followUp');
  const assessmentHandler = createWorkflowHandler('assessment');

  const confirmHandler = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    data,
  }: any) => {
    let workflowDataScope: any = null;
    let modifiedFields: any = null;

    if (data?.key && data?.resource) {
      workflowDataScope = buildMappingChecklistItem(
        checklistItem?.code,
        data.key,
        data.resource,
        userEmail
      );
      modifiedFields = { [data.key]: workflowDataScope };
    }

    const metadata = checklistItem?.metadata && JSON.parse(checklistItem.metadata);
    const { allowNonExistingInformation = null, message = null } = metadata?.ui?.data ?? {};

    const getData =
      dataMappingChecklistItem[metadata?.ui?.component?.typeComponent ?? 'default'] ?? null;

    if (!isEmpty(getData?.[getData.key])) {
      const { key } = getData;
      const data = getData[key];

      const dataScope = workflowInstanceData(
        workflowInstance,
        `checklistItem.${checklistItem.code}.modifiedFields.${key}`
      );

      if (Array.isArray(data)) {
        workflowDataScope = Array.isArray(dataScope) ? [...dataScope] : [];

        for (const newData of data) {
          const existingIndex = workflowDataScope?.findIndex(
            (item) => item?.fhirReference === newData?.fhirReference
          );

          if (existingIndex !== -1) {
            workflowDataScope[existingIndex] = newData;
          } else {
            workflowDataScope.push(newData);
          }
        }
      } else if (isObject(data)) {
        workflowDataScope = data;
      }

      modifiedFields = { [key]: workflowDataScope };
    }

    if (allowNonExistingInformation) {
      modifiedFields = { message: message };
    }

    if (isEmpty(workflowDataScope) && !allowNonExistingInformation) {
      await confirmDialog({
        key: 'this',
        render: () => <NonExistingInformation i18n={i18n} closeDialog={closeDialog} />,
      });
      return;
    }

    const payloadForUpdateWorkflowinstance = {
      referenceValue: carePlanId,
      referenceName: caseRecord?.workflowReferenceName,
    };

    try {
      await handlerGenericStepWorkflow(
        workflowInstance,
        caseRecord,
        payloadForUpdateWorkflowinstance,
        checklistItem ? checklistItem : checklistData,
        userEmail,
        modifiedFields
      );
    } catch (err) {
      console.log('error', err);
    } finally {
      await fetchCaseRecord(carePlanId);
      await fetchWorkflowData();
    }
  };

  const returnHandler = async (data: any) => {
    try {
      let workflowInstanceData = getParseColumn(workflowInstance, 'data');

      if (data?.scopeKey?.length > 0) {
        for (const key of data.scopeKey) {
          if (workflowInstanceData?.scope?.checklistItem?.[key]) {
            delete workflowInstanceData.scope.checklistItem[key];
          }

          await WorkflowService.deleteCheckItemSaved({
            checklistItemCode: key,
            workflowInstanceId: workflowInstance?.id,
            workflowStageId: data.toStage,
          });
        }
      }

      const payloadForUpdateWorkflowinstance = {
        referenceValue: carePlanId,
        referenceName: caseRecord?.workflowReferenceName,
        stage: { id: data.toStage },
        data: isEmpty(workflowInstanceData?.scope?.checklistItem)
          ? null
          : JSON.stringify(workflowInstanceData),
      };

      await WorkflowService.updateWorkflowInstance(payloadForUpdateWorkflowinstance);
    } catch (err) {
    } finally {
      await fetchCaseRecord(carePlanId);
      await fetchWorkflowData();
    }
  };

  const countContactClient =
    Array.isArray(contactAttempt) &&
    contactAttempt?.filter((e) => e.noteType === 'Case Note').length;

  const statusesTasks = useMemo(
    () =>
      getStatusCountByPathway(
        getTaskGridRows(
          taskFilterByActivityDefinition,
          taskStatuses?.asList?.() ?? [],
          [],
          configurations?.fhir
        ),
        componentsData?.task?.statusCountByPathway
      ),
    [taskFilterByActivityDefinition, taskStatuses, configurations?.fhir, componentsData]
  );

  const statusesGoals = useMemo(
    () =>
      getGoalStatusCountByPathway(
        getParentGoalGridRows(
          goals,
          tasks,
          conditions,
          groupedGoals,
          goalStatuses?.asList?.() ?? []
        ),
        componentsData?.goal?.statusCountByPathway
      ),
    [goals, tasks, conditions, groupedGoals, goalStatuses]
  );

  const lastReScreening = useMemo(() => {
    const latestTask = (taskReScreening ?? []).reduce((latest: WrappedTask | null, current) => {
      const currentDate = new Date(
        current?.lastModified || current?.meta?.lastUpdated || 0
      ).getTime();

      const latestDate = new Date(latest?.lastModified || latest?.meta?.lastUpdated || 0).getTime();

      return currentDate > latestDate ? current : latest;
    }, null);

    const lastUpdated = latestTask?.lastModified || latestTask?.meta?.lastUpdated;

    return lastUpdated
      ? `Last re-screening: ${parseShortFormat(lastUpdated)}`
      : 'Last re-screening: - ';
  }, [taskReScreening]);

  /*
   ** Initialization Case Workflow
   */
  const authUser = useAuth().getCurrentUser();

  const userOrganizations = useMemo(() => authUser?.organizations ?? [], [authUser?.organizations]);

  const caseWorkflowData: WorkflowData = useMemo(
    () => ({
      id: 1,
      recordId: carePlanId,
      recordReference: caseRecord?.workflowReferenceName,
      stage: {
        id: workflowStage,
      },
      caseRecord,
      workflowInstance,
    }),
    [workflowStage, workflowInstance, caseRecord]
  );

  const consentInfoItemData = useMemo(() => {
    const { consents: consentRegistry = [] } = caseOption ?? {};
    const data = getConsentOption(consents, consentRegistry);
    return data?.map((e) => ({
      title: e?.title,
      value: e?.sub,
    }));
  }, [consents, caseOption]);

  // 1. Define independent, reusable workflow actions based on component types
  const BaseHandlers = {
    assignOwner: {
      type: 'payload',
      handler: assignCaseItemHandler,
      data: allUsers,
    },
    contactAttempt: {
      type: 'payload',
      handler: contactAttemptsHandler,
      data: `[${countContactClient}/5]`,
    },
    contactPatient: {
      type: 'payload',
      handler: contactAttemptsHandler,
      data: true,
    },
    consentInformation: {
      type: 'payload',
      handler: handlerConsents,
      data: consentInfoItemData ? consentInfoItemData : true,
    },
    scheduleNatSession: {
      type: 'confirm',
      handler: handlerConfirmationScheduleAppointment,
      data: true,
    },
    followUpInterview: {
      type: 'payload',
      handler: followUpHandler,
      data: true,
    },
    completeAssessment: {
      type: 'payload',
      handler: assessmentHandler,
      data: true,
    },
    confirm: {
      type: 'confirm',
      handler: confirmHandler,
      data: false,
    },

    // Workflow transition actions
    setDisposition: {
      type: 'confirm',
      handler: () => setDisposition(true),
      data: true,
    },
    setReject: {
      type: 'confirm',
      handler: (open: boolean = true) => setReject(open),
      data: false,
    },
    setReturn: {
      type: 'return',
      handler: (data: any) => returnHandler(data),
      data: false,
    },
    setOpenDialog: {
      type: 'openDialog',
      handler: (open: boolean = true) => toggleModal('openDialog', open),
      data: false,
    },
  };

  // 2. Construct the WorkflowHandler with the actions and map legacy codes
  const WorkflowHandler = {
    ...BaseHandlers,

    // Backward compatibility mappings for legacy hardcoded step codes
    ASSIGN_CASE_ITEM: BaseHandlers.assignOwner,
    CONTACT_ATTEMPT_CASE_ITEM: BaseHandlers.contactAttempt,
    ASSIGN_OWNER_REMOTE_PATIENT_MONITORING_CASE_ITEM: BaseHandlers.assignOwner,
    CONSENT_INFORMATION_REMOTE_PATIENT_MONITORING_CASE_ITEM: BaseHandlers.consentInformation,
    SCHEDULE_NAT_SESSION_REMOTE_PATIENT_MONITORING_CASE_ITEM: BaseHandlers.scheduleNatSession,
    CONTACT_REMOTE_PATIENT_MONITORING_CASE_ITEM: BaseHandlers.contactPatient,
    ASSIGN_INCK_CASE_ITEM: BaseHandlers.assignOwner,
    FOLLOW_UP_INTERVIEW_INCK_CASE_ITEM: BaseHandlers.followUpInterview,
    CONTACT_INCK_CASE_ITEM: BaseHandlers.contactAttempt,
    ASSIGN_CARE_PLAN_ITEM: BaseHandlers.assignOwner,
    CONFIRM: BaseHandlers.confirm,
  };

  const WorkflowData = {
    disposition,
    reject,
    openDialog: modals.openDialog,
    openDispositionExternal,
  };

  const dataMappingChecklistItem = useMemo(
    () => ({
      ...buildMappingChecklistItem('ConditionsList', 'conditions', conditions, userEmail),
      ...buildMappingChecklistItem('ObservationDetails', 'observations', observations, userEmail),
      ...buildMappingChecklistItem('Goals', 'goals', goals, userEmail),
      ...buildMappingChecklistItem('Tasks', 'tasks', tasks, userEmail),
    }),
    [observations, goals, conditions, userEmail]
  );

  const taskMap = {
    tasks: tasks,
    filtered: taskFilterByActivityDefinition,
    all: allTasks,
    reScreening: taskReScreening,
  };

  const tasksStepRendererRef = useRef<(props: any) => JSX.Element | null>(() => null);
  const tasksStepComponent = useMemo(
    () => (props: any) => tasksStepRendererRef.current?.(props),
    []
  );

  tasksStepRendererRef.current = (props: any) => {
    const propOther = props?.props;
    const ownerFilterPR = practitionerRoles.filter(Boolean);
    const { owner, ...rest } = propOther?.filters ?? {};
    const filters = {
      ...rest,
      ...(owner && ownerFilterPR.length ? { owner: ownerFilterPR } : {}),
    };
    const {
      isPatient = true,
      isCarePlan = true,
      taskSource = 'filtered',
      activityDefinition = 'case',
      showAssessmentChecklistDataButton = false,
    } = propOther?.componentProps ?? {};
    const resolvedTasks = taskMap[taskSource] ?? [];

    return (
      <TasksByPathway
        permissions={{
          ...taskPermissions,
          ...(showAssessmentChecklistDataButton && { isAllowedToAdd: false }),
        }}
        taskActivityDefinitionContext={
          activityDefinition ? taskActivityDefinition?.[activityDefinition] : undefined
        }
        tasks={resolvedTasks}
        patient={isPatient ? (patient as WrappedPatient) : null}
        carePlan={isCarePlan ? carePlan : null}
        healthCareServices={healthCareServices}
        serviceRequests={serviceRequest}
        menuTaskHandlers={commonMenuTaskHandlers}
        onAssessmentSelect={handleAssessmentSelect}
        filterTasksFromChecklistItem={Object.keys(filters).length > 0 ? filters : undefined}
        isWorkflowContext={true}
        childrenButtons={
          showAssessmentChecklistDataButton ? (
            <AssessmentChecklistDataButton followUpHandler={followUpHandler} i18n={i18n} />
          ) : undefined
        }
      />
    );
  };

  registerSteps({
    ObservationDetails: () => (
      <Card>
        <ObservationDetails
          patient={patient}
          customQuery={{ 'based-on': carePlan?.id, patient: patient?.id }}
          showEmpty={!carePlan?.id || !patient?.id}
          permissions={{
            isAllowedAdd: checkAclValidation({ acls: [crsAcls.CRS.CASE.OBSERVATION.ADD] }),
            isAllowedEdit: checkAclValidation({ acls: [crsAcls.CRS.CASE.OBSERVATION.EDIT] }),
          }}
          handlerAction={() => fetchStatistics(carePlanId)}
        />
      </Card>
    ),
    ConditionsList: () => (
      <Card>
        <ConditionsList
          patient={patient}
          handler={() => fetchStatistics(carePlanId)}
          noWrap={false}
          position="static"
        />
      </Card>
    ),
    Goals: () => (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '10fr',
        }}
      >
        <GoalContextProvider isWorkflowContext={true}>
          <GoalsByPathway
            permissions={goalsPermissions}
            planDefinitions={planDefinitions}
            conditions={conditions}
            observations={observations}
            carePlan={carePlan}
            patient={patient}
            goals={goals}
            tasks={tasks}
            openCollapseExternal={true}
            isCollapsible={false}
          />
        </GoalContextProvider>
      </Box>
    ),
    OutboundReferrals: (props: any) => {
      const { props: propOther = null } = props ?? {};
      return (
        <PatientGenericServiceRequest
          patient={patient}
          carePlan={carePlan}
          refreshExternal={isFetchingServiceRequest}
          taskActivityDefinitionContext={taskActivityDefinition?.caseServiceRequest}
          permissions={serviceRequestPermissions}
          observations={observations}
          conditions={conditions}
          planDefinition={propOther?.planDefinition}
        />
      );
    },
    CareTeam: () => (
      <CaseCareTeamGrid
        permissions={careTeamPermissions}
        carePlan={carePlan as WrappedCarePlan}
        careTeams={careTeams}
        patient={patient as WrappedPatient}
        onSuccessfulCreation={() => {
          memoizedRefreshCareTeam(null);
          fetchWorkflowData();
        }}
        onSuccessfulEdit={memoizedRefreshCareTeam}
        onMemberUpdateSuccess={refreshCareTeams}
        openCollapseExternal={true}
        showAddButton={true}
        isCollapsible={false}
      />
    ),
    Tasks: tasksStepComponent,
    ContactAttempts: () => (
      <Card>
        <TableCustom
          data={Array.isArray(contactAttempt) && mapContactAttemptsCases(contactAttempt)}
          tableHead={translateTableHead(TABLE_HEAD_CONTACT, i18n, 'crs')}
          childrenButtons={
            <Grid container display={'flex'} sx={{ my: 1, px: 1 }}>
              <Grid item xs={12} display={'flex'} justifyContent={'flex-end'}>
                {casePermissions.isAllowedAddContactAttempt && (
                  <Button
                    size="small"
                    variant="contained"
                    sx={{ height: '36px' }}
                    startIcon={<Add />}
                    onClick={() => toggleModal('contactClientForm', true)}
                  >
                    {i18n('case.details.contactAttempts.buttoncontactAttempts', 'crs')}
                  </Button>
                )}
              </Grid>
            </Grid>
          }
        />
      </Card>
    ),
    TasksCountTab: ({ itemName, isValid, checklistItem, isRequired, ...props }: any) => {
      const icon = isValid ? <CheckSharp /> : isRequired ? <IconRequired /> : <PlayArrowSharp />;
      return (
        <Tab
          {...props}
          icon={icon}
          iconPosition="start"
          label={
            <>
              {itemName}
              <Box display={'flex'} flexDirection={'row'}>
                {Object.entries(statusesTasks).map(([severity, count]) => (
                  <SeverityStatusCount
                    key={severity}
                    count={count}
                    severity={severity as AlertSeverity}
                  />
                ))}
              </Box>
              {isRequired && 'Required'}
            </>
          }
        />
      );
    },
    GoalsCountTab: ({ itemName, isValid, checklistItem, isRequired, ...props }: any) => {
      const icon = isValid ? <CheckSharp /> : isRequired ? <IconRequired /> : <PlayArrowSharp />;
      return (
        <Tab
          {...props}
          icon={icon}
          iconPosition="start"
          label={
            <>
              {itemName}
              <Box display={'flex'} flexDirection={'row'}>
                {Object.entries(statusesGoals).map(([severity, count]) => (
                  <SeverityStatusCount
                    key={severity}
                    count={count}
                    severity={severity as AlertSeverity}
                  />
                ))}
              </Box>
            </>
          }
        />
      );
    },
    UntilDisposeTab: ({ itemName, isValid, checklistItem, isRequired, ...props }: any) => {
      const isValidParsed = JSON.parse(checklistItem.isValid);
      const message = isValidParsed?.response?.message || '';
      const icon = isValid ? <CheckSharp /> : isRequired ? <Lock /> : <PlayArrowSharp />;

      useEffect(() => {
        if (!untilDisposeShownRef.current && isValid) {
          untilDisposeShownRef.current = true;

          showCloseModal(workflowInstance, setOpenDispositionExternal, standardDialog, i18n, {
            closeWorkflow: true,
            message: 'deadlineReached',
          });
        }
      }, [isValid]);

      return (
        <Tab
          {...props}
          disabled={isRequired ?? false}
          icon={icon}
          iconPosition="start"
          label={
            <Stack alignItems="flex-start" direction="column">
              <Typography variant="button">{message}</Typography>
              <Typography variant="caption" color="GrayText">
                {isRequired && 'Required'}
              </Typography>
            </Stack>
          }
        />
      );
    },
    RevisitUnansweredNeedsTab: ({
      itemName,
      isValid,
      checklistItem,
      isRequired,
      ...props
    }: any) => {
      const revisitValidation = useRevisitTaskValidation(patientId, checklistData);
      const effectiveIsValid = revisitValidation.isValid;
      const icon = isValid ? <CheckSharp /> : isRequired ? <IconRequired /> : <PlayArrowSharp />;
      return (
        <Tab
          {...props}
          icon={icon}
          iconPosition="start"
          label={itemName}
          onClick={() => {
            if (!effectiveIsValid) {
              setChecklistData(checklistItem);
              toggleModal('followUp', true);
            }
          }}
        />
      );
    },
    ContactAttemptTab: ({ itemName, isValid, checklistItem, isRequired, ...props }: any) => {
      const { selected } = props;
      useEffect(() => {
        if (selected && checklistItem) {
          setChecklistData(checklistItem);
        }
      }, [selected, checklistItem]);
      const icon = isValid ? <CheckSharp /> : isRequired ? <IconRequired /> : <PlayArrowSharp />;
      return <Tab {...props} icon={icon} iconPosition="start" label={itemName} />;
    },
    TasksReScreeningTab: ({ itemName, isValid, checklistItem, isRequired, ...props }: any) => {
      const { selected } = props;
      useEffect(() => {
        if (selected && checklistItem) {
          setChecklistData(checklistItem);
        }
      }, [selected, checklistItem]);
      const icon = isValid ? <CheckSharp /> : isRequired ? <IconRequired /> : <PlayArrowSharp />;
      return <Tab {...props} icon={icon} iconPosition="start" label={lastReScreening} />;
    },
  });

  useEffect(() => {
    if (!carePlanId) return;
    fetchAssessments(carePlanId);
    fetchCaseRecord(carePlanId);
    fetchContactAttempt(carePlanId);
    fetchStatistics(carePlanId);
  }, [carePlanId]);

  const fetchCaseRecord = async (id: any) => {
    const result: any = await CaseService.getOne(id);
    setCaseRecord(result);
  };

  const fetchWorkflowData = useCallback(
    async (record?: string | null) => {
      const effectiveRecord = record ?? caseRecordRef.current;
      if (!effectiveRecord) return;

      const result = await WorkflowService.getInstance(carePlanId, effectiveRecord);

      setWorkflowInstance(result);
      setWorkflowStage(result?.stage?.id);
      setInitialWorkflow(result?.workFlow?.code);

      const components = result?.workFlow?.metadata
        ? JSON.parse(result.workFlow.metadata).components?.workflow
        : null;

      untilDisposeShownRef.current = false;

      setMetadataWorkflow(components);
    },
    [carePlanId]
  );

  useEffect(() => {
    if (!caseRecord) return;
    fetchWorkflowData(caseRecord?.workflowReferenceName);
  }, [caseRecord, fetchWorkflowData]);

  useEffect(() => {
    caseRecordRef.current = caseRecord?.workflowReferenceName ?? null;
  }, [caseRecord]);

  const fetchStatistics = async (carePlanId: string | null) => {
    if (!carePlanId) return;
    const result = await CaseService.getStatistics(carePlanId);
    setStatisticsData(result);
  };

  /*
   ** End Initialization Case Workflow
   */

  useEffect(() => {
    if (carePlan && isSuccessCarePlan) {
      if (carePlan?.subject?.reference) {
        setPatientId(getIdFromReference(carePlan.subject.reference));
      }
      setCommunicationsQuery({ ...communicationsQuery, 'part-of': carePlan.id });
    }
  }, [carePlan, isSuccessCarePlan]);

  const getAllUsers = useCallback(async () => {
    // Don't run if workflow instance is not loaded
    if (!workflowInstance?.scope) {
      return;
    }

    try {
      // Extract ACL requirements from workflow metadata
      const workflowMetadata = workflowInstance?.workFlow?.metadata
        ? JSON.parse(workflowInstance.workFlow.metadata)
        : {};
      const ownerACLs = workflowMetadata?.workflow?.ownerAllowedACLs;
      const includeOwnerAffiliatedOrganizations =
        workflowMetadata?.workflow?.includeOwnerAffiliatedOrganizations ?? false;

      let result;
      if (ownerACLs && ownerACLs.length > 0) {
        // Use ACL-based filtering when configuration exists
        const relatedACLs = getRelatedAcls(ownerACLs);
        result = await UserService.getAllUsersByAcls(relatedACLs);
      } else {
        let organizationIds: string[] = [...userOrganizations];
        // Fallback to existing behavior when no ACL configuration
        if (includeOwnerAffiliatedOrganizations) {
          const affiliations = await fetchOrganizationAffiliation(userOrganizations);
          organizationIds = [...new Set([...organizationIds, ...affiliations])];
        }
        result = await UserService.getUsersByOrgAndRole(organizationIds, []);
      }

      setAllUsers(
        sortBy(
          result.filter((e: any) => e.status === 'Active'),
          ['firstName', 'LastName']
        )
      );
    } catch (err) {
      console.log('error on fetching all users', err);
    }
  }, [workflowInstance, userOrganizations]);

  // Call getAllUsers when workflow instance is loaded
  useEffect(() => {
    if (workflowInstance?.scope) {
      getAllUsers();
      showCloseModal(workflowInstance, setOpenDispositionExternal, standardDialog, i18n);
    }
  }, [workflowInstance?.scope]);

  const fetchContactAttempt = async (fhirId: string) => {
    const result: any = await CaseService.getContactAttempt(fhirId);
    setContactAttempt(result);
  };

  const fetchAssessments = async (surveyId: any) => {
    const result: any = await ReferralService.assessments(surveyId, 'CarePlan');
    if (result && isArray(result)) {
      setAssessmentsForms(mapAssessmentsForms(result));
    }
  };

  const handleReAssingOwned = async (ownedSelected: string) => {
    try {
      const payload = {
        record: {
          ...workflowInstance,
          assigned: {
            selectedUser: ownedSelected,
          },
        },
      };

      await WorkflowService.reAssignOwner(payload);
      await fetchWorkflowData();
    } catch (err) {
      console.log('error on handleReAssingOwned');
    }
  };

  const handleCreateContactForm = async (data: any) => {
    let resultContactAttempt: any = {};

    const payloadForUpdateWorkflowinstance = {
      referenceValue: carePlanId,
      referenceName: caseRecord?.workflowReferenceName,
    };

    try {
      resultContactAttempt = await CaseService.createContactAttempt(data);
      if (!_.isEmpty(resultContactAttempt)) {
        const workflowDataScope =
          workflowInstanceData(
            workflowInstance,
            `checklistItem.${checklistData.code}.modifiedFields.contacts`
          ) ?? [];

        workflowDataScope.push({
          outcome: resultContactAttempt?.outcome,
          contactOn: resultContactAttempt?.contactOn,
          nextContactOn: resultContactAttempt?.nextContactOn,
          reasonNote: data?.reasonNote ?? '',
        });

        const modifiedFields = {
          contacts: workflowDataScope,
        };

        await handlerGenericStepWorkflow(
          workflowInstance,
          caseRecord,
          payloadForUpdateWorkflowinstance,
          checklistData,
          userEmail,
          modifiedFields
        );
      }
    } catch (err) {
      console.log('error on handleCreateContactForm markItemCompleted', err);
    } finally {
      await fetchContactAttempt(carePlanId || '');
      await fetchWorkflowData();
    }
  };

  const handlerConsentInformation = async (data: any) => {
    setRefreshConsents(true);

    const modifiedFields = buildModifiedFieldsFromData(data, userEmail, checklistData) ?? undefined;

    const additionalParams = patient ? { patientId: patient?.id } : null;

    const payloadForUpdateWorkflowinstance = {
      referenceValue: carePlanId,
      referenceName: caseRecord?.workflowReferenceName,
    };

    try {
      await handlerGenericStepWorkflow(
        workflowInstance,
        caseRecord,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields,
        additionalParams
      );
    } catch (err) {
      console.log('error', err);
      setRefreshConsents(false);
    } finally {
      await fetchCaseRecord(carePlanId);
      await fetchWorkflowData();
    }
  };

  const handlerConfirmScheduleAppointment = async (data: any) => {
    const modifiedFields = {
      date_hour: data.date_hour && new Date(data.date_hour).toISOString(),
      location: data.location,
    };

    const payloadForUpdateWorkflowinstance = {
      referenceValue: carePlanId,
      referenceName: caseRecord?.workflowReferenceName,
    };

    try {
      await handlerGenericStepWorkflow(
        workflowInstance,
        caseRecord,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields
      );
    } catch (err) {
      console.log('error', err);
    } finally {
      await fetchCaseRecord(carePlanId);
      await fetchWorkflowData();
    }
  };

  const handlerConfirmGeneric = async (data?: any) => {
    const modifiedFields = buildModifiedFieldsFromData(data, userEmail, checklistData) ?? undefined;

    const payloadForUpdateWorkflowinstance = {
      referenceValue: carePlanId,
      referenceName: caseRecord?.workflowReferenceName,
    };

    try {
      await handlerGenericStepWorkflow(
        workflowInstance,
        caseRecord,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields
      );
    } catch (err) {
      console.log('error', err);
    } finally {
      await fetchWorkflowData();
      await fetchCaseRecord(carePlanId);
    }
  };

  const handleCreateCommunicationFhir = async (data: any) => {
    let result: any = {};
    try {
      result = await createCommunication(data);

      const setNote = result?.map((x1: Communication) => ({
        id: `${x1.resourceType}/${x1.id}`,
        text: x1?.payload?.[0]?.contentString,
      }));
      const note = carePlan?.note ?? [];
      const setNoteCarePlan: Annotation[] = [...setNote, ...note];

      if (setNoteCarePlan && carePlan) {
        carePlan.note = setNoteCarePlan;
        handleUpdateCase(carePlan);
      }

      toggleModal('note', false);
      refreshCommunications();
    } catch (err) {
      console.log('error on handleCreateCommunicationFhir', err);
    }

    return result;
  };

  const handleUpdateCase = async (data: CarePlan) => {
    const id = data?.id;
    const isEditing = Boolean(id);

    let result: any = {};

    const carePlanAny: any = carePlan;
    const { parsed, ...newObject } = carePlanAny;

    if (isEditing) {
      result = await updateCarePlan(
        produce((newObject as CarePlan)!, (draft) => {
          draft.supportingInfo = data.supportingInfo;
          draft.note = data.note;
        })
      );
    }

    refreshCarePlan();

    return result;
  };

  const memoizedWorkflowInstanceRefresh = useCallback(
    (data?: any) => {
      fetchStatistics(carePlanId);
      if (data && data?.isWorkflowContext) fetchWorkflowData();
    },
    [carePlanId, fetchWorkflowData]
  );

  const memoizedRefreshTasksSimple = useCallback((data?: any) => {
    refreshTasks();
    refreshAllTasks();
    memoizedWorkflowInstanceRefresh(data);
  }, []);

  const memoizedRefreshTasks = useCallback((data?: any) => {
    refreshCarePlan();
    refreshTasks();
    refreshAllTasks();
    memoizedWorkflowInstanceRefresh(data);
  }, []);

  const memoizedRefreshCareTeam = useCallback((careTeam) => {
    refreshCarePlan();
    refreshCareTeams();
  }, []);

  const memoizedRefreshGoalsSimple = useCallback((data?: any) => {
    refreshGoals();
    memoizedWorkflowInstanceRefresh(data);
  }, []);

  const memoizedRefreshGoals = useCallback((data?: any) => {
    refreshCarePlan();
    memoizedRefreshGoalsSimple(data);
    refreshTasks();
  }, []);

  useEffect(() => {
    onEvent(CREATE_TASK, memoizedRefreshTasks);
    onEvent(UPDATE_TASK, memoizedRefreshTasksSimple);
    onEvent(CREATE_GOAL, memoizedRefreshGoals);
    onEvent(UPDATE_GOAL, memoizedRefreshGoalsSimple);
    return () => {
      removeEvent(CREATE_TASK, memoizedRefreshTasks);
      removeEvent(UPDATE_TASK, memoizedRefreshTasksSimple);
      removeEvent(CREATE_GOAL, memoizedRefreshGoals);
      removeEvent(UPDATE_GOAL, memoizedRefreshGoalsSimple);
    };
  }, []);

  const handlerComplementaryInfo = async (store: any) => {
    const payloadForUpdateWorkflowinstance = {
      referenceValue: workflowInstance?.referenceValue,
      referenceName: workflowInstance?.referenceName,
      data: JSON.stringify({
        scope: {
          checklistItem: {
            ...store,
          },
        },
      }),
    };

    try {
      await WorkflowService.updateWorkflowInstance(payloadForUpdateWorkflowinstance);
    } catch (err) {
      console.log('error on handleComplementaryInfo', err);
    } finally {
      await fetchCaseRecord(carePlanId);
      await fetchWorkflowData();
    }
  };

  const defaultStructure = useMemo(() => {
    const checklistItem = getParseColumn(workflowInstance, 'data')?.scope?.checklistItem;

    return {
      ...(checklistItem?.CONTACT_ATTEMPT_CASE_ITEM && {
        CONTACT_ATTEMPT_CASE_ITEM: {
          ...(checklistItem?.CONTACT_ATTEMPT_CASE_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.CONTACT_ATTEMPT_CASE_ITEM?.validatedOn
            ),
          }),
          modifiedFields: {
            ...(checklistItem?.CONTACT_ATTEMPT_CASE_ITEM?.modifiedFields?.contactOn && {
              contactOn: formattedDateTimeZone(
                checklistItem?.CONTACT_ATTEMPT_CASE_ITEM?.modifiedFields?.contactOn
              ),
            }),
            ...(checklistItem?.CONTACT_ATTEMPT_CASE_ITEM?.modifiedFields?.nextContactOn && {
              nextContactOn: formattedDateTimeZone(
                checklistItem?.CONTACT_ATTEMPT_CASE_ITEM?.modifiedFields?.nextContactOn
              ),
            }),
          },
        },
      }),
      ...(checklistItem?.CONSENT_INFORMATION_REMOTE_PATIENT_MONITORING_CASE_ITEM && {
        CONSENT_INFORMATION_REMOTE_PATIENT_MONITORING_CASE_ITEM: {
          ...(checklistItem?.CONSENT_INFORMATION_REMOTE_PATIENT_MONITORING_CASE_ITEM
            ?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.CONSENT_INFORMATION_REMOTE_PATIENT_MONITORING_CASE_ITEM?.validatedOn
            ),
          }),
        },
      }),
      ...(checklistItem?.SCHEDULE_NAT_SESSION_REMOTE_PATIENT_MONITORING_CASE_ITEM && {
        SCHEDULE_NAT_SESSION_REMOTE_PATIENT_MONITORING_CASE_ITEM: {
          ...(checklistItem?.SCHEDULE_NAT_SESSION_REMOTE_PATIENT_MONITORING_CASE_ITEM
            ?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.SCHEDULE_NAT_SESSION_REMOTE_PATIENT_MONITORING_CASE_ITEM?.validatedOn
            ),
          }),
        },
      }),
      ...(checklistItem?.FOLLOW_UP_INCK_CASE_ITEM && {
        FOLLOW_UP_INTERVIEW_INCK_CASE_ITEM: {
          ...(checklistItem?.FOLLOW_UP_INCK_CASE_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(
              checklistItem?.FOLLOW_UP_INCK_CASE_ITEM?.validatedOn
            ),
          }),
        },
      }),
      ...(checklistItem?.CONTACT_INCK_CASE_ITEM && {
        CONTACT_INCK_CASE_ITEM: {
          ...(checklistItem?.CONTACT_INCK_CASE_ITEM?.validatedOn && {
            validatedOn: formattedDateTimeZone(checklistItem?.CONTACT_INCK_CASE_ITEM?.validatedOn),
          }),
          modifiedFields: {
            ...(checklistItem?.CONTACT_INCK_CASE_ITEM?.modifiedFields?.contactOn && {
              contactOn: formattedDateTimeZone(
                checklistItem?.CONTACT_INCK_CASE_ITEM?.modifiedFields?.contactOn
              ),
            }),
            ...(checklistItem?.CONTACT_INCK_CASE_ITEM?.modifiedFields?.nextContactOn && {
              nextContactOn: formattedDateTimeZone(
                checklistItem?.CONTACT_INCK_CASE_ITEM?.modifiedFields?.nextContactOn
              ),
            }),
          },
        },
      }),
    };
  }, [workflowInstance]);

  const taskPermissions: TaskPermissions = useMemo(
    () => ({
      isAllowedToAdd: checkAclValidation({ acls: [crsAcls.CRS.CASE.TASKS.ADD] }),
      isAllowedToEdit: checkAclValidation({ acls: [crsAcls.CRS.CASE.TASKS.EDIT] }),
      notes: {
        isAllowedToAdd: checkAclValidation({ acls: [crsAcls.CRS.CASE.TASKS.NOTES.ADD] }),
        isAllowedToView: checkAclValidation({ acls: [crsAcls.CRS.CASE.TASKS.NOTES.VIEW] }),
      },
      isAllowedToAssignProgram: checkAclValidation({
        acls: [crsAcls.CRS.CASE.TASKS.HANDLER.ASSIGN_PROGRAM],
      }),
      isAllowedReferralInternal: checkAclValidation({
        acls: [crsAcls.CRS.CASE.TASKS.HANDLER.REFERRAL_INTERNAL],
      }),
      isAllowedReferralExternal: checkAclValidation({
        acls: [crsAcls.CRS.CASE.TASKS.HANDLER.REFERRAL_EXTERNAL],
      }),
      isAllowedAppointmentScheduling: checkAclValidation({
        acls: [crsAcls.CRS.CASE.TASKS.HANDLER.APPOINTMENT],
      }),
      isAllowedWarmHandoff: checkAclValidation({
        acls: [crsAcls.CRS.CASE.TASKS.HANDLER.WARM_HANDOFF],
      }),
    }),
    []
  );

  const goalsPermissions: GoalsPermissions = useMemo(
    () => ({
      isAllowedToAdd: checkAclValidation({ acls: [crsAcls.CRS.CASE.GOAL.ADD] }),
      isAllowedToEdit: checkAclValidation({ acls: [crsAcls.CRS.CASE.GOAL.EDIT] }),
      notes: {
        isAllowedToAdd: checkAclValidation({ acls: [crsAcls.CRS.CASE.GOAL.NOTES.ADD] }),
        isAllowedToView: checkAclValidation({ acls: [crsAcls.CRS.CASE.GOAL.NOTES.VIEW] }),
      },
      tasks: taskPermissions,
    }),
    [taskPermissions]
  );

  const careTeamPermissions = useMemo(
    () => ({
      isAllowedToAdd: checkAclValidation({
        acls: [crsAcls.CRS.CASE.CARE_TEAMS.ADD],
      }),
      isAllowedToEdit: checkAclValidation({
        acls: [crsAcls.CRS.CASE.CARE_TEAMS.EDIT],
      }),
      members: {
        isAllowedToAdd: checkAclValidation({
          acls: [crsAcls.CRS.CASE.CARE_TEAMS.MEMBERS.ADD],
        }),
        isAllowedToDelete: checkAclValidation({
          acls: [crsAcls.CRS.CASE.CARE_TEAMS.MEMBERS.DELETE],
        }),
        isAllowedToEdit: checkAclValidation({
          acls: [crsAcls.CRS.CASE.CARE_TEAMS.MEMBERS.EDIT],
        }),
      },
    }),
    []
  );

  const casePermissions = useMemo(
    () => ({
      isAllowedToEdit: checkAclValidation({ acls: [crsAcls.CRS.CASE.EDIT] }),
      isAllowedViewWorkflow: checkAclValidation({ acls: [crsAcls.CRS.CASE.WORKFLOW.VIEW] }),
      isAllowedViewStatistics: checkAclValidation({ acls: [crsAcls.CRS.CASE.STATISTICS.VIEW] }),
      isAllowedViewSummary: checkAclValidation({ acls: [crsAcls.CRS.CASE.SUMMARY.VIEW] }),
      isAllowedViewCaseDetails: checkAclValidation({ acls: [crsAcls.CRS.CASE.DETAILS.VIEW] }),
      isAllowedViewComplementaryInfo: checkAclValidation({
        acls: [crsAcls.CRS.CASE.COMPLEMENTARY_INFO.VIEW],
      }),
      isAllowedViewRelatedReferrals: checkAclValidation({
        acls: [crsAcls.CRS.CASE.RELATED_REFERRALS.VIEW],
      }),
      isAllowedViewConsent: checkAclValidation({ acls: [crsAcls.CRS.CASE.CONSENT.VIEW] }),
      isAllowedViewContactAttempt: checkAclValidation({
        acls: [crsAcls.CRS.CASE.CONTACT_ATTEMPTS.VIEW],
      }),
      isAllowedAddContactAttempt: checkAclValidation({
        acls: [crsAcls.CRS.CASE.CONTACT_ATTEMPTS.ADD],
      }),
      isAllowViewAppointment: checkAclValidation({ acls: [crsAcls.CRS.CASE.APPOINTMENT.VIEW] }),
      isAllowAddAppointment: checkAclValidation({ acls: [crsAcls.CRS.CASE.APPOINTMENT.ADD] }),
      isAllowEditAppointment: checkAclValidation({ acls: [crsAcls.CRS.CASE.APPOINTMENT.EDIT] }),
      isAllowdViewPatient: checkAclValidation({ acls: [crsAcls.CRS.CASE.PATIENT.VIEW] }),
      isAllowedViewPatientGroups: checkAclValidation({ acls: [crsAcls.CRS.CASE.GROUPS.VIEW] }),
      isAllowedViewPrintableCarePlan: checkAclValidation({
        acls: [crsAcls.CRS.CASE.DETAILS.PRINTABLE_CARE_PLAN],
      }),
      isAllowedViewAssessment: checkAclValidation({ acls: [crsAcls.CRS.CASE.ASSESSMENT.VIEW] }),
      isAllowedViewCondition: checkAclValidation({ acls: [crsAcls.CRS.CASE.CONDITION.VIEW] }),
      isAllowedViewObservation: checkAclValidation({ acls: [crsAcls.CRS.CASE.OBSERVATION.VIEW] }),
    }),
    []
  );

  const workflowPermissions = useMemo(
    () => ({
      isAllowedToViewConsent:
        casePermissions.isAllowedViewConsent && metadataWorkflow?.consentInformation,
      isAllowedToViewContactAttempt:
        casePermissions.isAllowedViewContactAttempt && metadataWorkflow?.contactAttempt,
    }),
    [
      metadataWorkflow,
      casePermissions.isAllowedViewConsent,
      casePermissions.isAllowedViewContactAttempt,
    ]
  );

  const serviceRequestPermissions: ServiceRequestsPermissions = useMemo(
    () => ({
      isAllowedToAdd: checkAclValidation({ acls: [crsAcls.CRS.CASE.SERVICE_REQUESTS.ADD] }),
      isAllowedToEdit: checkAclValidation({ acls: [crsAcls.CRS.CASE.SERVICE_REQUESTS.EDIT] }),
      isAllowedToViewNotes: checkAclValidation({
        acls: [crsAcls.CRS.CASE.SERVICE_REQUESTS.NOTES.VIEW],
      }),
      taskPermissions: {
        isAllowedToAdd: checkAclValidation({
          acls: [crsAcls.CRS.CASE.SERVICE_REQUESTS.TASKS.ADD],
        }),
        isAllowedToEdit: checkAclValidation({
          acls: [crsAcls.CRS.CASE.SERVICE_REQUESTS.TASKS.EDIT],
        }),
        isAllowedToAssignProgram: checkAclValidation({
          acls: [crsAcls.CRS.CASE.SERVICE_REQUESTS.TASKS.HANDLER.ASSIGN_PROGRAM],
        }),
        isAllowedReferralInternal: checkAclValidation({
          acls: [crsAcls.CRS.CASE.SERVICE_REQUESTS.TASKS.HANDLER.REFERRAL_INTERNAL],
        }),
        isAllowedReferralExternal: checkAclValidation({
          acls: [crsAcls.CRS.CASE.SERVICE_REQUESTS.TASKS.HANDLER.REFERRAL_EXTERNAL],
        }),
        isAllowedAppointmentScheduling: checkAclValidation({
          acls: [crsAcls.CRS.CASE.SERVICE_REQUESTS.TASKS.HANDLER.APPOINTMENT],
        }),
        isAllowedWarmHandoff: checkAclValidation({
          acls: [crsAcls.CRS.CASE.SERVICE_REQUESTS.TASKS.HANDLER.WARM_HANDOFF],
        }),
        notes: {
          isAllowedToView: checkAclValidation({
            acls: [crsAcls.CRS.CASE.SERVICE_REQUESTS.TASKS.NOTES.VIEW],
          }),
          isAllowedToAdd: checkAclValidation({
            acls: [crsAcls.CRS.CASE.SERVICE_REQUESTS.TASKS.NOTES.ADD],
          }),
        },
      },
    }),
    []
  );

  const tableHeadContactAttempt = translateTableHead(TABLE_HEAD_CONTACT, i18n, 'crs');

  const {
    openAssessmentFrame,
    selectedTaskAssessmentUrl,
    handleAssessmentSelect,
    onCancelAssessment,
  } = useTaskAssessment(patient, tasks, refreshTasks);

  return (
    <Page title={i18n('case.details.title', 'crs')}>
      <StandardDialogProvider>
        <Container maxWidth={themeStretch ? false : 'xl'}>
          <HeaderBreadcrumbs
            title={i18n('case.details.title', 'crs')}
            heading=""
            links={[
              { name: `${i18n('admin.list.dashboard')}`, href: PATH_DASHBOARD.root },
              { name: `${i18n('breadcrumbs.title', 'crs')}` },
              { name: `${i18n('case.list.title', 'crs')}`, href: PATH_DASHBOARD.crs.case },
              { name: caseRecord?.internalNumber ?? '' },
            ]}
          />
          <Grid container spacing={3}>
            {casePermissions?.isAllowedViewPrintableCarePlan && (
              <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'end' }}>
                <PrintableCarePlan carePlanId={carePlan?.id} />
              </Grid>
            )}

            {casePermissions?.isAllowedViewWorkflow && (
              <Grid item xs={12} sx={{ mt: 2 }}>
                {patient && metadataWorkflow?.workflowHeader && (
                  <WorkflowHeader patient={patient} configurations={configurations} i18n={i18n} />
                )}

                {casePermissions?.isAllowedViewStatistics && (
                  <WorkflowStatistics
                    workflowTitle={workflowInstance?.workFlow?.name ?? ''}
                    statisticsData={statisticsData}
                    path={'case.details'}
                    namespace={'crs'}
                    i18n={i18n}
                  />
                )}

                <Workflow
                  data={caseWorkflowData}
                  refreshRecord={() => {
                    fetchWorkflowData();
                    fetchStatistics(carePlanId);
                    setOpenDispositionExternal(false);
                  }}
                  initialWorkflow={initialWorkflow}
                  workflowHandler={WorkflowHandler}
                  workflowData={WorkflowData}
                  i18Path="case.details.workflow"
                  namespace="crs"
                />
              </Grid>
            )}

            {casePermissions?.isAllowedViewSummary && (
              <Grid item xs={12}>
                <Card>
                  <Typography sx={{ m: 4 }}>{i18n('case.details.caseSummary', 'crs')}</Typography>
                  <CaseSummary
                    tasks={taskFilterByActivityDefinition}
                    serviceRequest={getServiceRequestByFilters}
                    carePlan={carePlan}
                  />
                </Card>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                <Typography sx={{ mt: 1.5 }} variant="overline">
                  collapse all
                </Typography>
                <IconButton onClick={() => setOpenCollapse(!openCollapse)}>
                  <Iconify
                    icon={
                      openCollapse ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'
                    }
                  />
                </IconButton>
              </Box>
            </Grid>
            {casePermissions.isAllowedViewCaseDetails && (
              <Grid item xs={12} md={6} lg={6}>
                <CaseInformation
                  title={i18n('case.details.caseInfo.title', 'crs')}
                  caseRecord={caseRecord}
                  workflowInstance={workflowInstance}
                  users={allUsers}
                  onOwnedAssign={handleReAssingOwned}
                  openCollapseExternal={openCollapse}
                  isAllowedToEdit={casePermissions.isAllowedToEdit}
                />
              </Grid>
            )}

            {casePermissions?.isAllowedViewComplementaryInfo && (
              <Grid item xs={12} md={6} lg={6}>
                <ExpandableCard
                  title={i18n('case.details.caseComplementaryInfo.title', 'crs')}
                  openCollapseExternal={openCollapse}
                >
                  <ComplementaryInfo
                    dataSchema={getParseColumn(workflowInstance, 'dataSchema')}
                    data={getParseColumn(workflowInstance, 'data')}
                    defaultStructure={defaultStructure}
                    handleComplementaryInfo={handlerComplementaryInfo}
                    isAllowedToEdit={casePermissions.isAllowedToEdit}
                  />
                </ExpandableCard>
              </Grid>
            )}

            {patient && casePermissions?.isAllowdViewPatient && (
              <Grid item xs={12} md={6} lg={6}>
                <PatientSummary
                  title={i18n('case.details.patientInformation.title', 'crs')}
                  patient={patient}
                  openCollapseExternal={openCollapse}
                />
              </Grid>
            )}

            {patient && checkAclValidation({ acls: [crsAcls.CRS.CASE.RELATED_PERSONS.VIEW] }) && (
              <Grid item xs={12} md={6} lg={6}>
                <ExpandableCard title={i18n('patients.details.relatedPersons.title', 'crs')}>
                  <RelatedPersonList
                    patient={patient}
                    open={relatedPersonsOpen}
                    setOpen={setRelatedPersonsOpen}
                  />
                </ExpandableCard>
              </Grid>
            )}

            {checkAclValidation({ acls: [crsAcls.CRS.CASE.FAMILY_MEMBERS.VIEW] }) && (
              <Grid item xs={12} md={6} lg={6}>
                <FamilyMembers isOpen={openCollapse} carePlanId={carePlanId ?? ''} />
              </Grid>
            )}

            {patient && casePermissions?.isAllowedViewPatientGroups && (
              <Grid item xs={12} md={6} lg={6}>
                <ExpandableCard title={i18n('case.details.groups.title', 'crs')}>
                  <GroupsList patient={patient} PATH_PERMISIONS="CASE" />
                </ExpandableCard>
              </Grid>
            )}

            {casePermissions?.isAllowedViewRelatedReferrals && (
              <Grid item xs={12} md={6} lg={6}>
                <RelatedReferrals
                  isOpen={openCollapseRelatedReferrals}
                  referralIds={inboundReferralIds}
                  externalLoading={!carePlan}
                  patientId={patientId ?? ''}
                />
              </Grid>
            )}

            {/* <Grid item xs={12} md={6} lg={6}>
            <ReferralHistory
              patient={patient}
              carePlan={carePlan}
              openCollapseExternal={openCollapse}
            />
          </Grid> */}

            {checkAclValidation({ acls: [crsAcls.CRS.CASE.OUTBOUND_REFERRAL.VIEW] }) && (
              <Grid item xs={12}>
                <OutboundByPathway
                  patient={patient}
                  carePlan={carePlan}
                  serviceRequest={getServiceRequestByFilters}
                  healthCareServices={healthCareServices}
                  refreshServiceRequest={refreshServiceRequest}
                  createServiceRequest={createServiceRequest}
                  updateServiceRequest={updateServiceRequest}
                  openCollapseExternal={openCollapse}
                  permissions={serviceRequestPermissions}
                />
              </Grid>
            )}

            {checkAclValidation({ acls: [crsAcls.CRS.CASE.SERVICE_REQUESTS.VIEW] }) && (
              <Grid item xs={12}>
                <ExpandableCard
                  title={i18n('case.details.listServiceRequests.title', 'crs')}
                  openCollapseExternal={openCollapse}
                >
                  <PatientGenericServiceRequest
                    patient={patient}
                    carePlan={carePlan}
                    refreshExternal={isFetchingServiceRequest}
                    taskActivityDefinitionContext={taskActivityDefinition?.caseServiceRequest}
                    permissions={serviceRequestPermissions}
                    observations={observations}
                    conditions={conditions}
                  />
                </ExpandableCard>
              </Grid>
            )}

            {casePermissions?.isAllowedViewObservation && (
              <Grid item xs={12}>
                <ExpandableCard
                  title={i18n('case.details.listObservations.title', 'crs')}
                  openCollapseExternal={openCollapse}
                >
                  <ObservationDetails
                    patient={patient}
                    customQuery={{ 'based-on': carePlan?.id, patient: patient?.id }}
                    permissions={{
                      isAllowedAdd: checkAclValidation({
                        acls: [crsAcls.CRS.CASE.OBSERVATION.ADD],
                      }),
                      isAllowedEdit: checkAclValidation({
                        acls: [crsAcls.CRS.CASE.OBSERVATION.EDIT],
                      }),
                    }}
                    handlerAction={() => fetchStatistics(carePlanId)}
                  />
                </ExpandableCard>
              </Grid>
            )}
            {casePermissions?.isAllowedViewCondition && (
              <Grid item xs={12}>
                <ExpandableCard
                  title={i18n('case.details.listconditions.title', 'crs')}
                  openCollapseExternal={openCollapse}
                >
                  <ConditionsList patient={patient} handler={() => fetchStatistics(carePlanId)} />
                </ExpandableCard>
              </Grid>
            )}
            {checkAclValidation({ acls: [crsAcls.CRS.CASE.GOAL.VIEW] }) && (
              <Grid item xs={12}>
                <GoalsByPathway
                  permissions={goalsPermissions}
                  planDefinitions={planDefinitions}
                  conditions={conditions}
                  observations={observations}
                  carePlan={carePlan}
                  patient={patient}
                  goals={goals}
                  tasks={tasks}
                  openCollapseExternal={openCollapse}
                  title={i18n('case.details.listofGoals.title', 'crs')}
                />
              </Grid>
            )}
            {checkAclValidation({ acls: [crsAcls.CRS.CASE.TASKS.VIEW] }) && (
              <Grid item xs={12}>
                <CollapsibleProvider
                  title={i18n('case.details.listtasks.title', 'crs')}
                  openCollapseExternal={openCollapse}
                  isCollapsible={true}
                >
                  <TasksByPathway
                    permissions={taskPermissions}
                    taskActivityDefinitionContext={taskActivityDefinition?.case}
                    tasks={taskFilterByActivityDefinition}
                    patient={patient as WrappedPatient}
                    carePlan={carePlan}
                    healthCareServices={healthCareServices}
                    serviceRequests={serviceRequest}
                    menuTaskHandlers={commonMenuTaskHandlers}
                    onAssessmentSelect={handleAssessmentSelect}
                    conditions={conditions}
                    observations={observations}
                    carePlanTaskIds={carePlan?.getRelatedTaskIds()}
                    allTasksEnabled={true}
                  />
                </CollapsibleProvider>
              </Grid>
            )}
            {checkAclValidation({ acls: [crsAcls.CRS.CASE.CARE_TEAMS.VIEW] }) && (
              <Grid item xs={12}>
                <CaseCareTeamGrid
                  permissions={careTeamPermissions}
                  carePlan={carePlan as WrappedCarePlan}
                  careTeams={careTeams}
                  patient={patient as WrappedPatient}
                  onSuccessfulCreation={memoizedRefreshCareTeam}
                  onSuccessfulEdit={refreshCareTeams}
                  onMemberUpdateSuccess={refreshCareTeams}
                  openCollapseExternal={openCollapse}
                  showAddButton={true}
                  title={i18n('case.details.careteams.title', 'crs')}
                />
              </Grid>
            )}
            {workflowPermissions.isAllowedToViewConsent && (
              <Grid item xs={12}>
                <ExpandableCard
                  title={i18n('patients.details.consents.title', 'crs')}
                  openCollapseExternal={openCollapse}
                >
                  <ConsentGrid patient={patient} />
                </ExpandableCard>
              </Grid>
            )}
            {workflowPermissions.isAllowedToViewContactAttempt && (
              <Grid item xs={12}>
                <ExpandableCard
                  title={i18n('case.details.contactAttempts.title', 'crs')}
                  openCollapseExternal={openCollapse}
                >
                  <TableCustom
                    data={Array.isArray(contactAttempt) && mapContactAttemptsCases(contactAttempt)}
                    tableHead={tableHeadContactAttempt}
                    childrenButtons={
                      <Grid item xs={4} display={'flex'} justifyContent={'flex-end'}>
                        {casePermissions.isAllowedAddContactAttempt && (
                          <Button
                            variant="contained"
                            size="small"
                            sx={{ height: '36px' }}
                            startIcon={<Add />}
                            style={{ position: 'absolute', top: '25px', right: '25px' }}
                            onClick={() => toggleModal('contactClientForm', true)}
                          >
                            {i18n('case.details.contactAttempts.buttoncontactAttempts', 'crs')}
                          </Button>
                        )}
                      </Grid>
                    }
                  />
                </ExpandableCard>
              </Grid>
            )}
            {casePermissions?.isAllowedViewAssessment && (
              <Grid item xs={12}>
                <ExpandableCard
                  title={i18n('case.details.assessmentsForm.title', 'crs')}
                  openCollapseExternal={openCollapse}
                >
                  <TableCustom
                    data={Array.isArray(assessmentsForms) && assessmentsForms}
                    tableHead={translateTableHead(TABLE_HEAD_ASSESSMENTS, i18n, 'crs')}
                    childrenButtons={
                      <Grid item xs={4} display={'flex'} justifyContent={'flex-end'}>
                        {checkAclValidation({
                          acls: [crsAcls.CRS.CASE.ASSESSMENT.START],
                        }) && (
                          <StartAssessmentsButton
                            keyRegistry="crs-patient-assessments"
                            patientId={patient?.id ?? ''}
                            additionalQueryParams={`&caseUUID=${carePlanId}`}
                          />
                        )}
                      </Grid>
                    }
                  />
                </ExpandableCard>
              </Grid>
            )}
            {checkAclValidation({ acls: [crsAcls.CRS.CASE.APPOINTMENT.VIEW] }) && (
              <Grid item xs={12}>
                <ExpandableCard
                  title={i18n('case.details.appointments.title', 'crs')}
                  openCollapseExternal={openCollapse}
                >
                  <AppointmentList
                    patient={patient}
                    permissions={{
                      isAllowedAdd: casePermissions?.isAllowAddAppointment,
                      isAllowedEdit: casePermissions?.isAllowEditAppointment,
                    }}
                  />
                </ExpandableCard>
              </Grid>
            )}
            {checkAclValidation({ acls: [crsAcls.CRS.CASE.NOTES.VIEW] }) && (
              <Grid item xs={12}>
                <ExpandableCard
                  title={i18n('case.details.notesDetails.title', 'crs')}
                  openCollapseExternal={openCollapse}
                >
                  <NotesDisplayWithAdd
                    data={mapCommunicationsToNotesDisplay(communications, [
                      'notes_case',
                      'notes_task',
                      'notes_goal',
                    ])}
                    columns={CRS_NOTES_COLUMNS}
                    titleButton={
                      checkAclValidation({ acls: [crsAcls.CRS.CASE.NOTES.ADD] })
                        ? i18n('case.details.notesDetails.button', 'crs')
                        : undefined
                    }
                    isAllowedToAdd={checkAclValidation({ acls: [crsAcls.CRS.CASE.NOTES.ADD] })}
                    onAddNote={() => toggleModal('note', true)}
                    emptyMessage="No case notes available"
                  />
                </ExpandableCard>
              </Grid>
            )}
            {checkAclValidation({ acls: [crsAcls.CRS.CASE.ATTACHMENTS.VIEW] }) && (
              <Grid item xs={12}>
                <ExpandableCard
                  title={i18n('case.details.attachments.title', 'crs')}
                  openCollapseExternal={openCollapse}
                >
                  <Card style={{ boxShadow: 'none', position: 'static' }}>
                    <DocumentList
                      permissions={{
                        isAllowedToAdd: checkAclValidation({
                          acls: [crsAcls.CRS.CASE.ATTACHMENTS.ADD],
                        }),
                        isAllowedToEdit: checkAclValidation({
                          acls: [crsAcls.CRS.CASE.ATTACHMENTS.EDIT],
                        }),
                      }}
                      patient={patient}
                      documentReferences={documentReferences}
                      resource={carePlan}
                      showFilters={true}
                      refreshDocumentReference={refreshDocumentReference}
                      openCollapseExternal={openCollapse}
                      handleUpdateResource={handleUpdateCase}
                    />
                  </Card>
                </ExpandableCard>
              </Grid>
            )}
          </Grid>

          <NoteAddForm
            open={modals.note}
            patient={patient}
            onCancel={() => toggleModal('note', false)}
            resource={[carePlan]}
            handleCreate={handleCreateCommunicationFhir}
            typeNote="notes_case"
          />

          <ContactForm
            option={i18n('case.details.contactAttempts.buttoncontactAttempts', 'crs')}
            countRecord={countContactClient}
            checkListData={checklistData}
            referral={caseRecord}
            open={modals.contactClientForm}
            onCancel={() => toggleModal('contactClientForm', false)}
            handleCreateContactForm={handleCreateContactForm}
          />

          <ConsentInformation
            patient={patient}
            isLoading={isLoading}
            workflowInstanceData={getParseColumn(workflowInstance, 'data')}
            handlerSave={handlerConsentInformation}
            open={modals.consents}
            consentOption={caseOption?.consents ?? null}
            consents={consents}
            breadcrumbs={[
              `${i18n('case.details.title', 'crs')}`,
              `${i18n('case.details.workflow.step.intake', 'crs')}`,
            ]}
            onClose={() => toggleModal('consents', false)}
          />

          <ScheduleAppointment
            isOpen={modals.scheduleAppointment}
            handlerIsOpen={(val) => toggleModal('scheduleAppointment', val)}
            handlerScheduleAppointment={handlerConfirmScheduleAppointment}
          />

          <AssessmentFromChecklistData
            patient={patient}
            checkListData={checklistData}
            open={modals.followUp}
            onClose={() => {
              memoizedRefreshTasksSimple();
              toggleModal('followUp', false);
            }}
            handlerSave={handlerConfirmGeneric}
            basedOn={{ reference: `${carePlan?.resourceType}/${carePlan?.id}` }}
          />

          <AssessmentFromChecklistData
            patient={patient}
            checkListData={checklistData}
            open={modals.assessment}
            onClose={() => {
              toggleModal('assessment', false);
            }}
            handlerSave={handlerConfirmGeneric}
            basedOn={{ reference: `${carePlan?.resourceType}/${carePlan?.id}` }}
          />

          {selectedTaskAssessmentUrl && openAssessmentFrame ? (
            <AssessmentFrame
              open={openAssessmentFrame}
              url={selectedTaskAssessmentUrl}
              onCancel={onCancelAssessment}
            />
          ) : null}
        </Container>
      </StandardDialogProvider>
    </Page>
  );
}
